import { captureException, withScope } from "@sentry/react";
import type { BuildSources } from "@brickme/project-core/src/image-build/create-bricked-image-from-source.ts";
import type { BackgroundImageSource } from "@brickme/project-core/src/image-build/operation.ts";
import type {
	BrickedPictureOutput,
	Palette,
	PictureConfiguration,
} from "@brickme/project-core/src/model/picture.ts";
import type {
	BuildWorkerIncomingData,
	BuildWorkerOutgoingData,
} from "./build-worker-data.ts";
import { deserializeError } from "../utils/worker-error.ts";

type BrickedBuildListener = (result: BrickedPictureOutput) => void;

function createBuildWorkerBridge() {
	// Note: neither format of worker works locally for me atm. Either in a built preview or
	// in dev mode. Haven't tried an isolated hello world. It's working fine on dev though.
	const worker = new Worker(new URL("./build-worker.ts", import.meta.url), {
		type: "module",
	});

	const listeners: Array<BrickedBuildListener> = [];

	// postMessage structured-clones every (multi-MB) bitmap before the worker
	// can dedup, so skip identity-only changes here. Records are immutable -
	// the id list pins the content.
	let lastPostedBackgroundImageIds: readonly string[] | undefined;

	function onMessage(e: MessageEvent) {
		const data = e.data as BuildWorkerOutgoingData;
		switch (data.type) {
			case "bricked-result": {
				for (const listener of listeners) {
					listener(data.output);
				}
				break;
			}
			case "build-error": {
				// The build worker has no Sentry of its own, so a swallowed failure
				// means the build silently never arrives. Surface anything it forwards.
				const error = deserializeError(data.error);
				// eslint-disable-next-line no-console
				console.error(`Build worker error (${data.context})`, error);
				withScope((scope) => {
					scope.setTag("source", "build-worker");
					scope.setExtra("buildWorkerErrorContext", data.context);
					captureException(error);
				});
				break;
			}
			default:
				console.error("Unknown build message", data);
				throw new Error("Editor got unknown message");
		}
	}
	worker.addEventListener("message", onMessage);
	// Backstop for an uncaught worker error the worker code didn't forward itself.
	worker.onerror = (event: ErrorEvent): void => {
		// eslint-disable-next-line no-console
		console.error("Build worker uncaught error", event.message);
		withScope((scope) => {
			scope.setTag("source", "build-worker");
			scope.setExtra("buildWorkerErrorContext", "worker-uncaught");
			captureException(
				event.error ?? new Error(event.message || "Build worker error"),
			);
		});
	};

	return {
		addEventListener(_: "brickedBuild", listener: BrickedBuildListener) {
			listeners.push(listener);
		},
		removeEventListener(
			eventName: "brickedBuild",
			listener: BrickedBuildListener,
		) {
			const index = listeners.indexOf(listener);
			if (index === -1) {
				console.error(`Listener for ${eventName} not found`, listener);
				return;
			}

			listeners.splice(index, 1);
		},
		setBuildSources(sources: BuildSources | undefined) {
			worker.postMessage({
				type: "set-build-sources",
				sources,
			} satisfies BuildWorkerIncomingData);
		},
		clearPicture() {
			worker.postMessage({
				type: "clear-picture",
			} satisfies BuildWorkerIncomingData);
		},
		setPicture(picture: PictureConfiguration) {
			worker.postMessage({
				type: "set-picture",
				picture,
			} satisfies BuildWorkerIncomingData);
		},
		setPalette(palette: Palette | undefined) {
			worker.postMessage({
				type: "set-palette",
				palette,
			} satisfies BuildWorkerIncomingData);
		},
		setBackgroundImages(backgroundImages: readonly BackgroundImageSource[]) {
			const ids = backgroundImages.map((b) => b.id);
			if (
				lastPostedBackgroundImageIds !== undefined &&
				ids.length === lastPostedBackgroundImageIds.length &&
				ids.every((id, i) => id === lastPostedBackgroundImageIds?.[i])
			) {
				return;
			}
			lastPostedBackgroundImageIds = ids;
			worker.postMessage({
				type: "set-background-images",
				backgroundImages,
			} satisfies BuildWorkerIncomingData);
		},
		terminate() {
			worker.terminate();
		},
	};
}

type BuildWorker = ReturnType<typeof createBuildWorkerBridge>;

export type { BuildWorker };
export { createBuildWorkerBridge };
